"use client";

import { Component, useMemo } from "react";

import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

import type { ReactNode } from "react";

const Plot = dynamic(() => import("./createPlotlyComponent"), { ssr: false });

const PLOTLY_CONFIG: Partial<Plotly.Config> = {
  responsive: true,
  displayModeBar: "hover" as const,
  displaylogo: false,
};

function ChartErrorFallback() {
  const t = useTranslations("sidebar.activeNode.chart");
  return (
    <div className="flex items-center justify-center h-full text-xxs font-mono text-gray-400">
      {t("renderError")}
    </div>
  );
}

class PlotlyErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Plotly chart failed to render:", error);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return <ChartErrorFallback />;
    }
    return children;
  }
}

type Props = {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  width: number;
  height: number;
  className?: string;
};

export function PlotlyRenderer({ data, layout, width, height, className }: Props) {
  const mergedLayout = useMemo<Partial<Plotly.Layout>>(
    () => ({
      ...layout,
      width,
      height,
      margin: layout.margin ?? { l: 50, r: 30, t: 40, b: 50 },
    }),
    [layout, width, height],
  );

  return (
    <div className={className}>
      <PlotlyErrorBoundary>
        <Plot
          data={data}
          layout={mergedLayout}
          config={PLOTLY_CONFIG}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler
        />
      </PlotlyErrorBoundary>
    </div>
  );
}
